/**
 * MuhurtaPage — Muhurta auspicious timing calendar (मुहूर्त).
 *
 * v4 API: enriched MuhurtaWindow shape with scoreBreakdown, bilingual
 * explanation, meta (nakshatra/tithi/vara/rahuKalaFree), recommendation flag,
 * bookmark CRUD, iCal export, and event search across date ranges.
 */
import { useState, useMemo, type ElementType } from 'react'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  muhurtaApi,
  type MuhurtaWindow,
  type MuhurtaBookmark,
  type EventWindowsPayload,
  type EventWindowsResponse,
} from '@/lib/api'
import { useUser } from '@/store'
import { cn } from '@/lib/utils'
import { useFeatureGate, MUHURTA_FREE_LIMIT } from '@/hooks/useSubscription'
import { UsageGate } from '@/components/paywall'
import {
  Calendar, ChevronLeft, ChevronRight, RefreshCw,
  Clock, CheckCircle2, XCircle, AlertCircle, ChevronDown, ChevronUp,
  BarChart2, BookmarkPlus, BookmarkCheck, Download, Trash2, Star,
  Search, Sparkles,
} from 'lucide-react'
import dayjs from 'dayjs'

// ── Vedic lookup tables ───────────────────────────────────────────────────────

const NAKSHATRA_NAMES = [
  'Ashwini','Bharani','Krittika','Rohini','Mrigashira','Ardra','Punarvasu',
  'Pushya','Ashlesha','Magha','Purva Phalguni','Uttara Phalguni','Hasta',
  'Chitra','Swati','Vishakha','Anuradha','Jyeshtha','Mula','Purva Ashadha',
  'Uttara Ashadha','Shravana','Dhanishtha','Shatabhisha','Purva Bhadrapada',
  'Uttara Bhadrapada','Revati',
]

const TITHI_NAMES = [
  'Pratipada','Dwitiya','Tritiya','Chaturthi','Panchami','Shashthi','Saptami',
  'Ashtami','Navami','Dashami','Ekadashi','Dwadashi','Trayodashi','Chaturdashi',
  'Purnima/Amavasya',
]

const VARA_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']

// ── Quality config ────────────────────────────────────────────────────────────

const QUALITY_CONFIG = {
  excellent: {
    label: 'Excellent',
    dot:   'bg-auspicious',
    bar:   'bg-auspicious',
    text:  'text-auspicious',
    badge: 'bg-auspicious/10 text-auspicious border border-auspicious/20',
    row:   'border-l-auspicious/60',
    Icon:  CheckCircle2,
  },
  good: {
    label: 'Good',
    dot:   'bg-gold',
    bar:   'bg-gold',
    text:  'text-gold',
    badge: 'bg-gold/10 text-gold border border-gold/20',
    row:   'border-l-gold/60',
    Icon:  CheckCircle2,
  },
  fair: {
    label: 'Fair',
    dot:   'bg-star-soft',
    bar:   'bg-star-muted',
    text:  'text-star-soft',
    badge: 'bg-zinc-900/60 text-star-soft border border-zinc-800',
    row:   'border-l-zinc-700',
    Icon:  AlertCircle,
  },
  poor: {
    label: 'Poor',
    dot:   'bg-inauspicious',
    bar:   'bg-inauspicious',
    text:  'text-inauspicious',
    badge: 'bg-inauspicious/10 text-inauspicious border border-inauspicious/20',
    row:   'border-l-inauspicious/60',
    Icon:  XCircle,
  },
} as const

const QUALITY_ORDER: Array<MuhurtaWindow['qualityLabel']> = ['excellent', 'good', 'fair', 'poor']

// ── Score breakdown factors ───────────────────────────────────────────────────

const SCORE_FACTORS: Array<{ key: keyof MuhurtaWindow['scoreBreakdown']; label: string; sanskrit: string }> = [
  { key: 'panchaanga',      label: 'Panchanga',     sanskrit: 'पञ्चाङ्ग' },
  { key: 'nakshatraFit',    label: 'Nakshatra',     sanskrit: 'नक्षत्र' },
  { key: 'transitStrength', label: 'Transits',      sanskrit: 'गोचर' },
  { key: 'dashaHarmony',    label: 'Dasha',         sanskrit: 'दशा' },
  { key: 'yogas',           label: 'Yogas',         sanskrit: 'योग' },
  { key: 'lagnaStrength',   label: 'Lagna',         sanskrit: 'लग्न' },
]



// ── Day stats ─────────────────────────────────────────────────────────────────

function DayStats({ windows }: { windows: MuhurtaWindow[] }) {
  const counts = useMemo(() =>
    QUALITY_ORDER.map(q => ({ q, n: windows.filter(w => w.qualityLabel === q).length })),
    [windows]
  )
  const best = useMemo(() => Math.max(0, ...windows.map(w => w.score)), [windows])

  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      <div className="card-cosmic p-3 flex flex-col gap-1 sm:col-span-1">
        <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">Best Score</p>
        <p className="text-star-white text-xl font-mono font-bold">{best}</p>
        <p className="text-star-dim text-2xs">/ 100</p>
      </div>
      {counts.map(({ q, n }) => {
        const cfg = QUALITY_CONFIG[q]
        return (
          <div key={q} className="card-cosmic p-3 flex flex-col gap-1">
            <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">{cfg.label}</p>
            <p className={cn('text-xl font-mono font-bold', cfg.text)}>{n}</p>
            <p className="text-star-dim text-2xs">windows</p>
          </div>
        )
      })}
    </div>
  )
}

// ── Bookmark note dialog (inline) ─────────────────────────────────────────────

interface BookmarkNoteProps {
  onSave: (note: string) => void
  onCancel: () => void
  isSaving: boolean
}

function BookmarkNote({ onSave, onCancel, isSaving }: BookmarkNoteProps) {
  const [note, setNote] = useState('')
  return (
    <div className="mt-3 p-3 rounded-lg bg-zinc-950 border border-gold/30 space-y-2">
      <p className="text-gold text-2xs font-mono uppercase tracking-wider">Add a note (optional)</p>
      <input
        type="text"
        value={note}
        onChange={e => setNote(e.target.value)}
        placeholder="e.g. House warming ceremony"
        className="w-full bg-zinc-900/60 border border-zinc-800 rounded px-3 py-1.5 text-sm text-star-bright placeholder:text-star-dim focus:outline-none focus:border-gold/50"
        maxLength={120}
      />
      <div className="flex items-center gap-2">
        <button
          onClick={() => onSave(note)}
          disabled={isSaving}
          className="px-3 py-1 rounded text-2xs font-medium bg-gold/10 text-gold border border-gold/20 hover:bg-gold/20 transition-colors disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : 'Bookmark'}
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1 rounded text-2xs text-star-dim hover:text-star-soft border border-zinc-800 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

// ── Window row ────────────────────────────────────────────────────────────────

interface WindowRowProps {
  window:             MuhurtaWindow
  isExpanded:         boolean
  isCurrentlyActive:  boolean
  isBookmarked:       boolean
  onToggle:           () => void
  onBookmark:         (note: string) => void
  isBookmarking:      boolean
}

function WindowRow({
  window: win, isExpanded, isCurrentlyActive, isBookmarked,
  onToggle, onBookmark, isBookmarking,
}: WindowRowProps) {
  const cfg    = QUALITY_CONFIG[win.qualityLabel]
  const start  = dayjs(win.startUtc).format('HH:mm')
  const end    = dayjs(win.endUtc).format('HH:mm')
  const dur    = dayjs(win.endUtc).diff(dayjs(win.startUtc), 'minute')

  const nakName = NAKSHATRA_NAMES[win.meta.nakshatra - 1] ?? `#${win.meta.nakshatra}`
  const titName = TITHI_NAMES[((win.meta.tithi - 1) % 15)] ?? `#${win.meta.tithi}`
  const varaName = VARA_NAMES[win.meta.vara] ?? `Vara ${win.meta.vara}`

  const [showNote, setShowNote] = useState(false)
  const [lang, setLang] = useState<'en' | 'hi'>('en')

  return (
    <div
      className={cn(
        'rounded-xl border-l-2 transition-colors duration-150 overflow-hidden',
        'bg-zinc-900/50 border border-zinc-800',
        cfg.row,
        isCurrentlyActive && 'ring-1 ring-gold/30',
      )}
    >
      {/* Main row */}
      <div className="flex items-center gap-3 px-4 pt-3 pb-2">
        {/* Time + duration */}
        <button
          onClick={onToggle}
          className="flex-shrink-0 w-28 text-left hover:opacity-80 transition-opacity"
          aria-expanded={isExpanded}
        >
          <p className="text-star-bright text-sm font-mono leading-none">{start} – {end}</p>
          <p className="text-star-dim text-2xs mt-1">{dur}m</p>
        </button>

        {/* Badges */}
        <button
          onClick={onToggle}
          className="flex items-center gap-2 flex-shrink-0 flex-wrap text-left"
          aria-expanded={isExpanded}
        >
          <span className={cn('px-2 py-0.5 rounded text-2xs font-medium', cfg.badge)}>
            {cfg.label}
          </span>
          {win.recommendation && (
            <span className="px-2 py-0.5 rounded text-2xs bg-violet/10 text-violet-glow border border-violet/20 flex items-center gap-1">
              <Star className="w-2.5 h-2.5" />
              Top pick
            </span>
          )}
          {isCurrentlyActive && (
            <span className="px-2 py-0.5 rounded text-2xs bg-gold/10 text-gold border border-gold/20 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
              Now
            </span>
          )}
        </button>

        {/* Score bar */}
        <button
          onClick={onToggle}
          className="flex-1 flex items-center gap-3 min-w-0 text-left"
        >
          <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all duration-500', cfg.bar)}
              style={{ width: `${win.score}%` }}
            />
          </div>
          <span className={cn('text-sm font-mono font-semibold flex-shrink-0 w-8 text-right', cfg.text)}>
            {win.score}
          </span>
        </button>

        {/* Bookmark button */}
        <button
          onClick={() => {
            if (isBookmarked) return
            setShowNote(v => !v)
          }}
          className={cn(
            'flex-shrink-0 p-1.5 rounded-lg border transition-all',
            isBookmarked
              ? 'bg-gold/10 border-gold/20 text-gold cursor-default'
              : 'bg-zinc-900/60 border-zinc-800 text-star-muted hover:text-gold hover:border-gold/30',
          )}
          title={isBookmarked ? 'Bookmarked' : 'Bookmark this window'}
        >
          {isBookmarked
            ? <BookmarkCheck className="w-3.5 h-3.5" />
            : <BookmarkPlus className="w-3.5 h-3.5" />
          }
        </button>

        {/* Expand toggle */}
        <button
          onClick={onToggle}
          className="flex-shrink-0 text-star-dim"
          aria-expanded={isExpanded}
        >
          {isExpanded
            ? <ChevronUp className="w-4 h-4" />
            : <ChevronDown className="w-4 h-4" />
          }
        </button>
      </div>

      {/* Bookmark note (inline) */}
      {showNote && !isBookmarked && (
        <div className="px-4 pb-3">
          <BookmarkNote
            onSave={(note) => { onBookmark(note); setShowNote(false) }}
            onCancel={() => setShowNote(false)}
            isSaving={isBookmarking}
          />
        </div>
      )}

      {/* Expanded detail */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-zinc-800/50 pt-4 space-y-4">

          {/* Explanation — bilingual */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">Analysis</p>
              <div className="flex items-center gap-1">
                {(['en', 'hi'] as const).map(l => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={cn(
                      'px-2 py-0.5 rounded text-2xs font-mono border transition-all',
                      lang === l
                        ? 'bg-violet/10 text-violet-glow border-violet/20'
                        : 'bg-zinc-900/60 text-star-dim border-zinc-800',
                    )}
                  >
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <p className={cn('text-sm leading-relaxed', lang === 'hi' ? 'font-display' : 'text-star-soft')}>
              {win.explanation[lang]}
            </p>
          </div>

          {/* Score breakdown */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <BarChart2 className="w-3 h-3 text-star-dim" />
              <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">Score Breakdown</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SCORE_FACTORS.map(({ key, label, sanskrit }) => {
                const val = win.scoreBreakdown[key]
                return (
                  <div key={key} className="bg-zinc-900/60 rounded-lg p-2.5 space-y-1.5">
                    <div className="flex items-baseline justify-between gap-1">
                      <span className="text-star-bright text-xs font-medium">{label}</span>
                      <span className="text-star-dim text-2xs font-display">{sanskrit}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1 rounded-full bg-zinc-800 overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded-full',
                            val >= 75 ? 'bg-auspicious' : val >= 50 ? 'bg-gold' : 'bg-star-muted'
                          )}
                          style={{ width: `${val}%` }}
                        />
                      </div>
                      <span className="text-2xs font-mono text-star-muted flex-shrink-0 w-7 text-right">{val}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Vedic meta */}
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Nakshatra', value: nakName },
              { label: 'Tithi',     value: titName },
              { label: 'Vara',      value: varaName },
              {
                label: 'Rahu Kaal',
                value: win.meta.isRahuKalaFree ? 'Free ✓' : 'Active',
                cls:   win.meta.isRahuKalaFree ? 'text-auspicious' : 'text-inauspicious',
              },
            ].map(({ label, value, cls }) => (
              <div key={label} className="px-3 py-1.5 rounded-lg bg-zinc-900/60 border border-zinc-800">
                <span className="text-star-dim text-2xs font-mono">{label}: </span>
                <span className={cn('text-xs font-medium', cls ?? 'text-star-soft')}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Bookmarks panel ───────────────────────────────────────────────────────────

interface BookmarksPanelProps {
  bookmarks:    MuhurtaBookmark[]
  onDelete:     (id: string) => void
  isDeleting:   boolean
  getIcalUrl:   (id: string) => string
}

function BookmarksPanel({ bookmarks, onDelete, isDeleting, getIcalUrl }: BookmarksPanelProps) {
  if (bookmarks.length === 0) {
    return (
      <div className="flex flex-col items-center py-8 text-center">
        <span className="text-3xl mb-2">🔖</span>
        <p className="text-star-soft text-sm">No bookmarks yet.</p>
        <p className="text-star-dim text-2xs mt-1">Click the bookmark icon on any window to save it.</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {bookmarks.map(bm => (
        <div
          key={bm.id}
          className="flex items-center gap-3 p-3 rounded-xl bg-zinc-900/50 border border-zinc-800"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-star-bright text-sm font-mono">
                {dayjs(bm.windowStartUtc).format('D MMM · HH:mm')} – {dayjs(bm.windowEndUtc).format('HH:mm')}
              </span>
              <span className="text-gold text-2xs font-mono">{bm.score}/100</span>
            </div>
            {bm.note && (
              <p className="text-star-dim text-2xs mt-0.5 truncate">{bm.note}</p>
            )}
            <p className="text-star-dim text-2xs font-mono mt-0.5 capitalize">{bm.eventType}</p>
          </div>
          <a
            href={getIcalUrl(bm.id)}
            download
            className="flex-shrink-0 p-1.5 rounded-lg bg-zinc-900/60 border border-zinc-800 text-star-muted hover:text-auspicious hover:border-auspicious/30 transition-colors"
            title="Download .ics calendar file"
          >
            <Download className="w-3.5 h-3.5" />
          </a>
          <button
            onClick={() => onDelete(bm.id)}
            disabled={isDeleting}
            className="flex-shrink-0 p-1.5 rounded-lg bg-zinc-900/60 border border-zinc-800 text-star-muted hover:text-inauspicious hover:border-inauspicious/30 transition-colors disabled:opacity-40"
            title="Delete bookmark"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  )
}

// ── Event search panel ────────────────────────────────────────────────────────

const EVENT_TYPES = [
  { value: 'marriage',          label: 'Marriage',          sanskrit: 'विवाह' },
  { value: 'business_launch',   label: 'Business Launch',   sanskrit: 'व्यापार आरंभ' },
  { value: 'career',            label: 'Career',            sanskrit: 'करियर' },
  { value: 'travel',            label: 'Travel',            sanskrit: 'यात्रा' },
  { value: 'medical_procedure', label: 'Medical Procedure', sanskrit: 'चिकित्सा' },
  { value: 'education_start',   label: 'Education Start',   sanskrit: 'विद्यारंभ' },
] as const

const DOW_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function EventSearchPanel() {
  const user        = useUser()
  const qc          = useQueryClient()

  // ── form state ──────────────────────────────────────────────────────────────
  const [eventType,       setEventType]       = useState<string>('marriage')
  const [startDate,       setStartDate]       = useState(() => dayjs().format('YYYY-MM-DD'))
  const [endDate,         setEndDate]         = useState(() => dayjs().add(30, 'day').format('YYYY-MM-DD'))
  const [minScore,        setMinScore]        = useState(60)
  const [timePref,        setTimePref]        = useState<'' | 'morning' | 'afternoon' | 'evening'>('')
  const [daysOfWeek,      setDaysOfWeek]      = useState<Set<number>>(new Set())

  // ── results state ───────────────────────────────────────────────────────────
  const [results,         setResults]         = useState<EventWindowsResponse | null>(null)
  const [expandedId,      setExpandedId]      = useState<string | null>(null)
  const [bookmarkingId,   setBookmarkingId]   = useState<string | null>(null)
  const [bookmarkedIds,   setBookmarkedIds]   = useState<Map<string, string>>(new Map())

  const windowKey = (w: MuhurtaWindow) => `${w.startUtc}_${w.endUtc}`

  // ── search mutation ─────────────────────────────────────────────────────────
  const search = useMutation({
    mutationFn: (payload: EventWindowsPayload) => muhurtaApi.searchWindows(payload),
    onSuccess: (res) => {
      setResults(res)
      setExpandedId(null)
    },
  })

  // ── bookmark mutations ──────────────────────────────────────────────────────
  const createBookmark = useMutation({
    mutationFn: (vars: { key: string; win: MuhurtaWindow }) =>
      muhurtaApi.createBookmark({
        windowStartUtc: vars.win.startUtc,
        windowEndUtc:   vars.win.endUtc,
        eventType,
        score:          vars.win.score,
      }),
    onSuccess: (bm, vars) => {
      setBookmarkedIds(prev => new Map(prev).set(vars.key, bm.id))
      qc.invalidateQueries({ queryKey: ['muhurta-bookmarks'] })
    },
    onSettled: () => setBookmarkingId(null),
  })

  const deleteBookmark = useMutation({
    mutationFn: (id: string) => muhurtaApi.deleteBookmark(id),
    onSuccess: (_, id) => {
      setBookmarkedIds(prev => {
        const next = new Map(prev)
        for (const [k, v] of next.entries()) if (v === id) next.delete(k)
        return next
      })
      qc.invalidateQueries({ queryKey: ['muhurta-bookmarks'] })
    },
    onSettled: () => setBookmarkingId(null),
  })

  const handleBookmark = (win: MuhurtaWindow, _note: string) => {
    const key = windowKey(win)
    if (bookmarkedIds.has(key)) {
      const bmId = bookmarkedIds.get(key)!
      setBookmarkingId(key)
      deleteBookmark.mutate(bmId)
    } else {
      setBookmarkingId(key)
      createBookmark.mutate({ key, win })
    }
  }

  // ── toggle day-of-week ──────────────────────────────────────────────────────
  const toggleDow = (d: number) => {
    setDaysOfWeek(prev => {
      const next = new Set(prev)
      next.has(d) ? next.delete(d) : next.add(d)
      return next
    })
  }

  // ── submit ──────────────────────────────────────────────────────────────────
  const handleSearch = () => {
    // Birth data: combine user's birthDate + birthTime into a UTC-ish string.
    // TODO: proper tz→UTC conversion once User gains lat/lon.
    const birthDateTimeUtc =
      user?.profile?.dateOfBirth && user?.profile?.timeOfBirth
        ? `${user.profile.dateOfBirth}T${user.profile.timeOfBirth}.000Z`
        : '1990-01-01T01:30:00.000Z'

    // TODO: swap for user lat/lon when available (same as Choghadiya widget)
    const loc = { latitude: 28.6139, longitude: 77.209, timezone: user?.profile?.birthTimezone ?? 'Asia/Kolkata' }

    search.mutate({
      eventType,
      dateRangeStart: `${startDate}T00:00:00.000Z`,
      dateRangeEnd:   `${endDate}T23:59:59.999Z`,
      location:       loc,
      birthData: {
        birthDateTimeUtc,
        latitude:  loc.latitude,
        longitude: loc.longitude,
        timezone:  loc.timezone,
      },
      minScore:       minScore > 0 ? minScore : undefined,
      timePreference: timePref  || undefined,
      daysOfWeek:     daysOfWeek.size > 0 ? [...daysOfWeek] : undefined,
    })
  }

  const eventLabel = EVENT_TYPES.find(e => e.value === eventType)?.label ?? eventType

  return (
    <div className="space-y-5">

      {/* Search form */}
      <div className="card-cosmic p-5 space-y-5">

        {/* Event type */}
        <div className="space-y-2">
          <label className="text-star-dim text-2xs font-mono uppercase tracking-wider block">Event type</label>
          <div className="flex flex-wrap gap-1.5">
            {EVENT_TYPES.map(et => (
              <button
                key={et.value}
                onClick={() => setEventType(et.value)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-all',
                  eventType === et.value
                    ? 'bg-violet/20 text-violet-glow border-violet/30 font-medium'
                    : 'bg-zinc-900/60 text-star-muted border-zinc-800 hover:text-star-bright',
                )}
              >
                {et.label}
                <span className="font-display text-2xs opacity-60">{et.sanskrit}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Date range */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-star-dim text-2xs font-mono uppercase tracking-wider block">From</label>
            <input
              type="date"
              value={startDate}
              min={dayjs().format('YYYY-MM-DD')}
              onChange={e => setStartDate(e.target.value)}
              className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-star-bright focus:outline-none focus:border-violet/50 font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-star-dim text-2xs font-mono uppercase tracking-wider block">To</label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={e => setEndDate(e.target.value)}
              className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-star-bright focus:outline-none focus:border-violet/50 font-mono"
            />
          </div>
        </div>

        {/* Optional filters row */}
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center pt-1 border-t border-zinc-800/50">

          {/* Min score */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-star-dim text-2xs font-mono whitespace-nowrap">
              Min score: <span className="text-star-soft">{minScore}</span>
            </span>
            <input
              type="range" min={0} max={90} step={10}
              value={minScore}
              onChange={e => setMinScore(Number(e.target.value))}
              className="w-24 accent-violet"
            />
          </div>

          {/* Time preference */}
          <div className="flex items-center gap-1.5">
            <span className="text-star-dim text-2xs font-mono mr-0.5">Time:</span>
            {([
              { v: '',          l: 'Any'  },
              { v: 'morning',   l: 'AM'   },
              { v: 'afternoon', l: 'PM'   },
              { v: 'evening',   l: 'Eve'  },
            ] as Array<{ v: typeof timePref; l: string }>).map(({ v, l }) => (
              <button
                key={v}
                onClick={() => setTimePref(v)}
                className={cn(
                  'px-2 py-0.5 rounded text-2xs border transition-all',
                  timePref === v
                    ? 'bg-gold/20 text-gold border-gold/30'
                    : 'bg-zinc-900/60 text-star-dim border-zinc-800 hover:text-star-soft',
                )}
              >
                {l}
              </button>
            ))}
          </div>

          {/* Days of week */}
          <div className="flex items-center gap-1 sm:ml-auto">
            <span className="text-star-dim text-2xs font-mono mr-0.5">Days:</span>
            {DOW_LABELS.map((d, i) => (
              <button
                key={i}
                onClick={() => toggleDow(i)}
                className={cn(
                  'w-6 h-6 rounded text-2xs font-mono border transition-all',
                  daysOfWeek.has(i)
                    ? 'bg-auspicious/20 text-auspicious border-auspicious/30'
                    : 'bg-zinc-900/60 text-star-dim border-zinc-800 hover:text-star-soft',
                )}
              >
                {d}
              </button>
            ))}
            {daysOfWeek.size > 0 && (
              <button
                onClick={() => setDaysOfWeek(new Set())}
                className="text-2xs text-star-dim hover:text-star-soft ml-0.5 transition-colors"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleSearch}
          disabled={search.isPending}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet/20 text-violet-glow border border-violet/30 hover:bg-violet/30 transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {search.isPending
            ? <RefreshCw className="w-4 h-4 animate-spin" />
            : <Search className="w-4 h-4" />
          }
          {search.isPending ? 'Searching…' : 'Find auspicious windows'}
        </button>

        {search.isError && (
          <p className="text-inauspicious text-sm">
            Search failed — showing sample results if available.
          </p>
        )}
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-4">

          {/* Summary bar */}
          <div className="flex items-center justify-between">
            <p className="text-star-soft text-sm">
              <span className="text-star-white font-semibold">{results.windows.length}</span>
              {' '}windows found for{' '}
              <span className="text-violet-glow font-medium">{eventLabel}</span>
              {' '}· scored from {results.totalWindowsScored} candidates
            </p>
          </div>

          {/* Best window highlight */}
          {results.bestWindow && (
            <div className="card-cosmic border border-gold/30 p-4 space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-gold" />
                <span className="text-gold text-sm font-semibold">Best window</span>
              </div>
              <WindowRow
                window={results.bestWindow}
                isExpanded={expandedId === `best_${windowKey(results.bestWindow)}`}
                isCurrentlyActive={false}
                isBookmarked={bookmarkedIds.has(windowKey(results.bestWindow))}
                onToggle={() => {
                  const k = `best_${windowKey(results.bestWindow!)}`
                  setExpandedId(prev => prev === k ? null : k)
                }}
                onBookmark={(note) => handleBookmark(results.bestWindow!, note)}
                isBookmarking={bookmarkingId === windowKey(results.bestWindow)}
              />
            </div>
          )}

          {/* All windows */}
          {results.windows.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center">
              <span className="text-3xl mb-3">🔍</span>
              <p className="text-star-soft text-sm">No windows match your filters.</p>
              <p className="text-star-dim text-xs mt-1">Try widening the date range or lowering the minimum score.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">All windows</p>
              {results.windows.map(win => {
                const key = windowKey(win)
                return (
                  <WindowRow
                    key={key}
                    window={win}
                    isExpanded={expandedId === key}
                    isCurrentlyActive={false}
                    isBookmarked={bookmarkedIds.has(key)}
                    onToggle={() => setExpandedId(prev => prev === key ? null : key)}
                    onBookmark={(note) => handleBookmark(win, note)}
                    isBookmarking={bookmarkingId === key}
                  />
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Week-strip calendar ───────────────────────────────────────────────────────

/**
 * Shows 7 days centred on `selectedDate`. Each cell fetches the day's windows
 * in parallel and colours itself based on the best window's qualityLabel.
 */
function WeekStrip({
  selectedDate,
  onSelect,
}: {
  selectedDate: dayjs.Dayjs
  onSelect: (d: dayjs.Dayjs) => void
}) {
  // Generate the 7-day window: 3 before → selected → 3 after
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => selectedDate.subtract(3 - i, 'day')),
    [selectedDate],
  )

  const queries = useQueries({
    queries: days.map(d => ({
      queryKey: ['muhurta', d.format('YYYY-MM-DD')],
      queryFn: () => muhurtaApi.getWindows({ date: d.format('YYYY-MM-DD') }),
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  })

  const today = dayjs()

  return (
    <div className="flex gap-1.5">
      {days.map((d, i) => {
        const { data, isLoading } = queries[i]
        const isSelected = d.isSame(selectedDate, 'day')
        const isToday    = d.isSame(today, 'day')
        const isPast     = d.isBefore(today, 'day')

        // Derive best quality for the day
        let qualityDot: string | null = null
        if (data && data.length > 0) {
          const best = data.reduce((b, w) =>
            QUALITY_ORDER.indexOf(w.qualityLabel) < QUALITY_ORDER.indexOf(b.qualityLabel) ? w : b
          )
          qualityDot = QUALITY_CONFIG[best.qualityLabel].dot
        }

        return (
          <button
            key={d.format('YYYY-MM-DD')}
            onClick={() => onSelect(d)}
            className={cn(
              'flex flex-col items-center gap-1 py-2 px-1 rounded-xl flex-1 transition-all duration-150 border',
              isSelected
                ? 'bg-cosmic-aurora border-violet/40 shadow-glow-violet'
                : isToday
                  ? 'bg-cosmic-dusk border-gold/30 hover:bg-cosmic-aurora hover:border-violet/30'
                  : 'bg-cosmic-nebula/60 border-white/5 hover:bg-cosmic-dusk hover:border-white/10',
              isPast && !isSelected && 'opacity-60',
            )}
            aria-pressed={isSelected}
            aria-label={d.format('ddd D MMM')}
          >
            <span className="text-2xs font-mono uppercase tracking-wider text-star-dim">
              {d.format('ddd')}
            </span>
            <span className={cn(
              'text-sm font-semibold',
              isSelected ? 'text-star-white' : isToday ? 'text-gold' : 'text-star-bright',
            )}>
              {d.format('D')}
            </span>
            {/* Quality dot */}
            <div className="h-1.5 w-1.5 rounded-full">
              {isLoading ? (
                <div className="h-1.5 w-1.5 rounded-full shimmer" />
              ) : qualityDot ? (
                <div className={cn('h-1.5 w-1.5 rounded-full', qualityDot)} />
              ) : (
                <div className="h-1.5 w-1.5 rounded-full bg-star-dim/30" />
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}

// ── Date navigation ───────────────────────────────────────────────────────────

interface DateNavProps {
  date: dayjs.Dayjs
  onPrev: () => void
  onNext: () => void
  onToday: () => void
  isToday: boolean
}

function DateNav({ date, onPrev, onNext, onToday, isToday }: DateNavProps) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onPrev}
        className="p-1.5 rounded-lg bg-zinc-900/60 border border-zinc-800 text-star-muted hover:text-star-bright transition-colors"
        aria-label="Previous day"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-900/50 border border-zinc-800">
        <Calendar className="w-3.5 h-3.5 text-gold flex-shrink-0" />
        <span className="text-star-bright text-sm font-mono whitespace-nowrap">
          {date.format('ddd, D MMM YYYY')}
        </span>
      </div>

      <button
        onClick={onNext}
        className="p-1.5 rounded-lg bg-zinc-900/60 border border-zinc-800 text-star-muted hover:text-star-bright transition-colors"
        aria-label="Next day"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      {!isToday && (
        <button
          onClick={onToday}
          className="px-3 py-1.5 rounded-lg bg-zinc-900/60 border border-zinc-800 text-star-muted hover:text-star-bright text-xs font-mono transition-colors"
        >
          Today
        </button>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

type PageTab = 'windows' | 'bookmarks' | 'search'

export function MuhurtaPage() {
  const gate = useFeatureGate('muhurta')
  const qc = useQueryClient()

  const [date, setDate] = useState<dayjs.Dayjs>(() => dayjs())
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [qualityFilter, setQualityFilter] = useState<Set<MuhurtaWindow['qualityLabel']>>(new Set())
  const [minScore, setMinScore] = useState(0)
  const [timePreference, setTimePreference] = useState<'all' | 'morning' | 'afternoon' | 'evening'>('all')
  const [pageTab, setPageTab] = useState<PageTab>('windows')
  const [bookmarkingId, setBookmarkingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [bookmarkedIds, setBookmarkedIds] = useState<Map<string, string>>(new Map()) // windowKey → bookmarkId

  const dateStr = date.format('YYYY-MM-DD')
  const isToday = date.isSame(dayjs(), 'day')
  const now = dayjs()

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['muhurta', dateStr],
    queryFn: () => muhurtaApi.getWindows({ date: dateStr }),
    retry: 1,
  })

  const { data: bookmarksData } = useQuery({
    queryKey: ['muhurta-bookmarks'],
    queryFn: () => muhurtaApi.getBookmarks(),
    retry: 1,
  })

  const createBookmark = useMutation({
    mutationFn: (vars: { key: string; startUtc: string; endUtc: string; score: number; note: string }) =>
      muhurtaApi.createBookmark({
        windowStartUtc: vars.startUtc,
        windowEndUtc:   vars.endUtc,
        eventType:      'general',
        score:          vars.score,
        note:           vars.note || undefined,
      }),
    onSuccess: (bm, vars) => {
      setBookmarkedIds(prev => new Map(prev).set(vars.key, bm.id))
      qc.invalidateQueries({ queryKey: ['muhurta-bookmarks'] })
    },
    onSettled: () => setBookmarkingId(null),
  })

  const deleteBookmark = useMutation({
    mutationFn: (id: string) => muhurtaApi.deleteBookmark(id),
    onSuccess: (_, id) => {
      setBookmarkedIds(prev => {
        const next = new Map(prev)
        for (const [k, v] of next.entries()) if (v === id) next.delete(k)
        return next
      })
      qc.invalidateQueries({ queryKey: ['muhurta-bookmarks'] })
    },
    onSettled: () => setDeletingId(null),
  })

  const windows = data ?? []
  const bookmarks = bookmarksData ?? []

  const windowKey = (w: MuhurtaWindow) => `${w.startUtc}_${w.endUtc}`

  const activeWindowId = useMemo(() => {
    if (!isToday) return null
    const active = windows.find(w =>
      now.isAfter(dayjs(w.startUtc)) && now.isBefore(dayjs(w.endUtc))
    )
    return active ? windowKey(active) : null
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windows, isToday])

  const filteredWindows = useMemo(() => {
    let result = [...windows].sort((a, b) => dayjs(a.startUtc).diff(dayjs(b.startUtc)))
    if (qualityFilter.size > 0)
      result = result.filter(w => qualityFilter.has(w.qualityLabel))
    if (minScore > 0)
      result = result.filter(w => w.score >= minScore)
    if (timePreference !== 'all') {
      result = result.filter(w => {
        const h = dayjs(w.startUtc).hour()
        if (timePreference === 'morning')   return h >= 4  && h < 12
        if (timePreference === 'afternoon') return h >= 12 && h < 17
        if (timePreference === 'evening')   return h >= 17 || h < 4
        return true
      })
    }
    return result
  }, [windows, qualityFilter, minScore, timePreference])

  const toggleQualityFilter = (q: MuhurtaWindow['qualityLabel']) => {
    setQualityFilter(prev => {
      const next = new Set(prev)
      next.has(q) ? next.delete(q) : next.add(q)
      return next
    })
  }

  const handleBookmark = (win: MuhurtaWindow, note: string) => {
    const key = windowKey(win)
    setBookmarkingId(key)
    createBookmark.mutate({ key, startUtc: win.startUtc, endUtc: win.endUtc, score: win.score, note })
  }

  const handleDeleteBookmark = (id: string) => {
    setDeletingId(id)
    deleteBookmark.mutate(id)
  }

  return (
    <div className="max-w-4xl space-y-6">

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-star-white text-xl font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gold" />
            Muhurta Calendar
            <span className="text-gold text-sm font-display">मुहूर्त</span>
          </h1>
          <p className="text-star-muted text-sm mt-0.5">
            Auspicious windows — six-factor classical analysis
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="p-2 rounded-lg bg-zinc-900/60 border border-zinc-800 text-star-muted hover:text-star-bright transition-colors disabled:opacity-40"
            aria-label="Refresh"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isLoading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {isError && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-inauspicious/10 border border-inauspicious/20">
          <span className="text-inauspicious text-sm">Could not load muhurta data. Please try again.</span>
          <button onClick={() => refetch()} className="text-xs text-gold hover:text-gold-bright transition-colors">Retry</button>
        </div>
      )}

      {/* Usage gate — shows remaining free lookups for free tier users */}
      {!gate.isLoading && gate.remainingUses !== null && (
        <UsageGate remaining={gate.remainingUses} limit={MUHURTA_FREE_LIMIT} featureName="Muhurta lookups" />
      )}

      {/* Page tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-zinc-900/50 border border-zinc-800 w-fit">
        {([
          { id: 'windows',   label: 'Windows',   icon: Calendar },
          { id: 'bookmarks', label: 'Bookmarks', icon: BookmarkCheck },
          { id: 'search',    label: 'Search',    icon: Search },
        ] as Array<{ id: PageTab; label: string; icon: ElementType }>).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setPageTab(id)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
              pageTab === id
                ? 'bg-zinc-800/60 text-star-white'
                : 'text-star-muted hover:text-star-bright hover:bg-zinc-900/60',
            )}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
            {id === 'bookmarks' && bookmarks.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-2xs bg-gold/20 text-gold font-mono">
                {bookmarks.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Bookmarks tab ── */}
      {pageTab === 'bookmarks' && (
        <BookmarksPanel
          bookmarks={bookmarks}
          onDelete={handleDeleteBookmark}
          isDeleting={deletingId !== null}
          getIcalUrl={muhurtaApi.getIcalUrl}
        />
      )}

      {/* ── Search tab ── */}
      {pageTab === 'search' && <EventSearchPanel />}

      {/* ── Windows tab ── */}
      {pageTab === 'windows' && (
        <>
          {/* Week-strip calendar */}
          <WeekStrip
            selectedDate={date}
            onSelect={d => { setDate(d); setExpandedId(null) }}
          />

          {/* Date navigation */}
          <DateNav
            date={date}
            onPrev={() => { setDate(d => d.subtract(1, 'day')); setExpandedId(null) }}
            onNext={() => { setDate(d => d.add(1, 'day')); setExpandedId(null) }}
            onToday={() => { setDate(dayjs()); setExpandedId(null) }}
            isToday={isToday}
          />

          {/* Day stats */}
          <DayStats windows={windows} />

          {/* Filters */}
          <div className="space-y-3">
            {/* Quality filter chips */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-star-dim text-2xs font-mono mr-1">Quality:</span>
              {QUALITY_ORDER.map(q => {
                const cfg = QUALITY_CONFIG[q]
                const active = qualityFilter.has(q)
                return (
                  <button
                    key={q}
                    onClick={() => toggleQualityFilter(q)}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-2xs font-medium border transition-all',
                      active
                        ? cfg.badge
                        : 'bg-zinc-900/60 text-star-dim border-zinc-800 hover:text-star-soft',
                    )}
                  >
                    <span className={cn('w-1.5 h-1.5 rounded-full', cfg.dot)} />
                    {cfg.label}
                  </button>
                )
              })}
              {qualityFilter.size > 0 && (
                <button
                  onClick={() => setQualityFilter(new Set())}
                  className="px-2 py-1 rounded-full text-2xs text-star-dim hover:text-star-soft border border-zinc-800 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Min score + time preference */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="flex items-center gap-2">
                <span className="text-star-dim text-2xs font-mono whitespace-nowrap">Min score: <span className="text-star-soft">{minScore}</span></span>
                <input
                  type="range" min={0} max={90} step={10}
                  value={minScore}
                  onChange={e => setMinScore(Number(e.target.value))}
                  className="w-24 accent-gold"
                />
              </div>
              <div className="flex items-center gap-1.5 sm:ml-auto">
                <span className="text-star-dim text-2xs font-mono mr-1">Time:</span>
                {([
                  { v: 'all',       l: 'All' },
                  { v: 'morning',   l: 'Morning' },
                  { v: 'afternoon', l: 'Afternoon' },
                  { v: 'evening',   l: 'Evening' },
                ] as Array<{ v: typeof timePreference; l: string }>).map(({ v, l }) => (
                  <button
                    key={v}
                    onClick={() => setTimePreference(v)}
                    className={cn(
                      'px-2.5 py-1 rounded-full text-2xs font-medium border transition-all',
                      timePreference === v
                        ? 'bg-violet/20 text-violet-glow border-violet/30'
                        : 'bg-zinc-900/60 text-star-dim border-zinc-800 hover:text-star-soft',
                    )}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Current time indicator */}
          {isToday && activeWindowId && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gold/5 border border-gold/20">
              <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
              <span className="text-gold text-2xs font-mono">
                {now.format('HH:mm')} — active window running
              </span>
            </div>
          )}

          {/* Window list */}
          <div className="space-y-2">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-5 h-5 text-star-dim animate-spin" />
              </div>
            )}

            {!isLoading && filteredWindows.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <span className="text-3xl mb-3">📅</span>
                <p className="text-star-soft text-sm">No windows match the current filters.</p>
                <button
                  onClick={() => { setQualityFilter(new Set()); setMinScore(0); setTimePreference('all') }}
                  className="mt-3 text-gold text-xs hover:text-gold-bright transition-colors"
                >
                  Clear filters
                </button>
              </div>
            )}

            {!isLoading && filteredWindows.map(win => {
              const key = windowKey(win)
              return (
                <WindowRow
                  key={key}
                  window={win}
                  isExpanded={expandedId === key}
                  isCurrentlyActive={key === activeWindowId}
                  isBookmarked={bookmarkedIds.has(key)}
                  onToggle={() => setExpandedId(prev => prev === key ? null : key)}
                  onBookmark={(note) => handleBookmark(win, note)}
                  isBookmarking={bookmarkingId === key}
                />
              )
            })}
          </div>

          {filteredWindows.length > 0 && (
            <p className="text-star-dim text-2xs text-center font-mono leading-relaxed pt-2 border-t border-zinc-800/50">
              Scores computed from six classical Jyotish factors: Panchanga · Nakshatra · Transits · Dasha · Yogas · Lagna
            </p>
          )}
        </>
      )}
    </div>
  )
}
